import * as React from "react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Check, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface OptionType {
  label: string
  value: string
}

interface MultiSelectProps<T extends OptionType> {
  options: T[]
  selected: T["value"][]
  onChange: (selected: T["value"][]) => void
  onClose?: (selected: T["value"][]) => void
  placeholder?: string
  className?: string
  /** Show the selected options' own labels (truncated) instead of the
   * placeholder + count — off by default so existing callers are unaffected. */
  showSelectedLabels?: boolean
  /** When set, adds a top row with this label that clears the whole
   * selection — undefined (the default) omits it, unaffecting existing callers. */
  allLabel?: string
}

export function MultiSelect<T extends OptionType>({
  options,
  selected,
  onChange,
  onClose,
  placeholder,
  className,
  showSelectedLabels = false,
  allLabel,
}: MultiSelectProps<T>) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)

  const handleSelect = (value: T["value"]) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open)
      if (!open && onClose) {
        onClose(selected)
      }
    },
    [onClose, selected],
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="default"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-between rounded-md border px-4 py-2 pr-2.5 text-left font-medium hover:bg-blue-100 dark:hover:bg-blue-800",
            className,
          )}
        >
          <span className="truncate text-foreground">
            {showSelectedLabels && selected.length > 0
              ? options
                  .filter((option) => selected.includes(option.value))
                  .map((option) => option.label)
                  .join(", ")
              : (placeholder ?? t("app.ui.multiSelect.placeholder"))}
            {!showSelectedLabels && selected.length > 0 && (
              <span> ({selected.length})</span>
            )}
          </span>
          <ChevronDown className="ml-2.5 h-4 w-4 shrink-0 text-gray-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[200] w-[200px] p-0" align="start">
        <Command>
          {options.length > 10 && (
            <CommandInput placeholder={t("app.ui.multiSelect.search")} />
          )}
          <CommandList>
            <CommandEmpty>{t("app.ui.multiSelect.empty")}</CommandEmpty>
            <CommandGroup>
              {allLabel && (
                <CommandItem value={allLabel} onSelect={() => onChange([])}>
                  {selected.length === 0 && <Check className="mr-2 h-4 w-4" />}
                  {allLabel}
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                >
                  {selected.includes(option.value) && (
                    <Check className="mr-2 h-4 w-4" />
                  )}
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
